import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { FileText, Package, Send, CheckCircle, XCircle, Truck, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { DetailLayout, DetailList, RailCard } from '@/components/common/DetailPanels';
import {
  PageHeader,
  DataTable,
  Column,
  StatusBadge,
  MetricCard,
  LoadingState,
  ConfirmDialog,
  KPIStrip,
  MilestoneTimeline,
} from '@/components/common';
import {
  purchaseOrdersApi,
  PurchaseOrderWithShipments,
  PurchaseOrderItemWithShipped,
  shipmentsApi,
  CreateShipmentDto,
  locationsApi,
} from '@/api/services';
import {
  poVisibilityApi,
  type PoMilestone,
  type UpdateMilestoneDto,
} from '@/api/services/po-visibility.api';
import { getErrorMessage } from '@/api/client';
import { useAuth } from '@/context/AuthContext';
import { formatCurrency as formatMoney } from '@/lib/utils';

const statusMap: Record<
  string,
  'draft' | 'pending' | 'approved' | 'rejected' | 'processing' | 'completed'
> = {
  DRAFT: 'draft',
  SUBMITTED: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  ORDERED: 'processing',
  PARTIALLY_RECEIVED: 'processing',
  RECEIVED: 'completed',
  CANCELLED: 'rejected',
};

const statusLabels: Record<string, string> = {
  DRAFT: 'Draft',
  SUBMITTED: 'Pending Approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  ORDERED: 'Ordered',
  PARTIALLY_RECEIVED: 'Partially Received',
  RECEIVED: 'Received',
  CANCELLED: 'Cancelled',
};

export default function PurchaseOrderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState<PurchaseOrderWithShipments | null>(null);
  const [milestones, setMilestones] = useState<PoMilestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    action?: 'submit' | 'issue' | 'cancel';
  }>({ open: false });
  const [processing, setProcessing] = useState(false);

  // Shipment creation state
  const [shipmentSheetOpen, setShipmentSheetOpen] = useState(false);
  const [shipmentFormData, setShipmentFormData] = useState<{
    originId: string;
    destinationId: string;
    carrier: string;
    trackingNumber: string;
    estimatedDeparture: string;
    estimatedArrival: string;
    totalWeight: string;
    totalVolume: string;
    items: Array<{
      itemId: string;
      item?: { sku: string; name: string; unit: string };
      quantity: number;
      maxQuantity: number;
    }>;
  }>({
    originId: '',
    destinationId: '',
    carrier: '',
    trackingNumber: '',
    estimatedDeparture: '',
    estimatedArrival: '',
    totalWeight: '',
    totalVolume: '',
    items: [],
  });
  const [locations, setLocations] = useState<any[]>([]);
  const [savingShipment, setSavingShipment] = useState(false);

  const fetchData = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await purchaseOrdersApi.getWithShipments(id);
      setOrder(data);

      // Milestones are tracked separately from the order itself, and an order
      // with none is normal — a failure here should not cost the reader the
      // order.
      const timeline = await poVisibilityApi
        .getMilestones(id)
        .catch(() => ({ data: [] as PoMilestone[] }));
      setMilestones(timeline.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
      navigate('/purchase-orders');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [id, fetchData]);

  const handleUpdateMilestone = async (milestoneId: string, data: UpdateMilestoneDto) => {
    try {
      await poVisibilityApi.updateMilestone(milestoneId, data);
      toast.success('Milestone updated');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleAction = async () => {
    if (!order || !actionDialog.action) return;

    try {
      setProcessing(true);
      switch (actionDialog.action) {
        case 'submit':
          await purchaseOrdersApi.submit(order.id);
          toast.success('PO submitted for approval');
          break;
        case 'issue':
          await purchaseOrdersApi.issue(order.id);
          toast.success('PO issued to supplier');
          break;
        case 'cancel':
          await purchaseOrdersApi.cancel(order.id);
          toast.success('PO cancelled');
          break;
      }
      setActionDialog({ open: false });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setProcessing(false);
    }
  };

  const handleOpenShipmentSheet = async () => {
    if (!order) return;

    // Calculate outstanding items
    const outstandingItems = order.items
      .filter((item) => item.quantity > item.shippedQty)
      .map((item) => ({
        itemId: item.itemId,
        item: item.item,
        quantity: item.quantity - item.shippedQty,
        maxQuantity: item.quantity - item.shippedQty,
      }));

    if (outstandingItems.length === 0) {
      toast.info('All items have been shipped');
      return;
    }

    // Pre-populate form
    setShipmentFormData((prev) => ({
      ...prev,
      items: outstandingItems,
    }));

    // Load locations for selection
    try {
      const locationsData = await locationsApi.getAll({ isActive: true });
      setLocations(locationsData.data || locationsData);
    } catch (error) {
      toast.error('Failed to load locations');
    }

    // Open sheet
    setShipmentSheetOpen(true);
  };

  const handleCreateShipment = async () => {
    if (!order) return;

    // Validation
    if (!shipmentFormData.originId) {
      toast.error('Please select an origin location');
      return;
    }
    if (!shipmentFormData.destinationId) {
      toast.error('Please select a destination location');
      return;
    }
    if (shipmentFormData.originId === shipmentFormData.destinationId) {
      toast.error('Origin and destination must be different');
      return;
    }
    if (shipmentFormData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    if (shipmentFormData.items.some((item) => item.quantity <= 0)) {
      toast.error('All items must have quantity greater than 0');
      return;
    }

    try {
      setSavingShipment(true);

      // Prepare DTO
      const createDto: CreateShipmentDto = {
        companyId: user?.companyId || '',
        originId: shipmentFormData.originId,
        destinationId: shipmentFormData.destinationId,
        carrier: shipmentFormData.carrier || undefined,
        trackingNumber: shipmentFormData.trackingNumber || undefined,
        estimatedDeparture: shipmentFormData.estimatedDeparture || undefined,
        estimatedArrival: shipmentFormData.estimatedArrival || undefined,
        totalWeight: shipmentFormData.totalWeight
          ? parseFloat(shipmentFormData.totalWeight)
          : undefined,
        totalVolume: shipmentFormData.totalVolume
          ? parseFloat(shipmentFormData.totalVolume)
          : undefined,
        items: shipmentFormData.items.map((item) => ({
          itemId: item.itemId,
          quantity: item.quantity,
        })),
        purchaseOrderId: order.id,
      };

      // Create shipment
      const newShipment = await shipmentsApi.create(createDto);

      toast.success(`Shipment ${newShipment.shipmentNumber} created successfully`);

      // Close sheet
      setShipmentSheetOpen(false);

      // Reset form
      setShipmentFormData({
        originId: '',
        destinationId: '',
        carrier: '',
        trackingNumber: '',
        estimatedDeparture: '',
        estimatedArrival: '',
        totalWeight: '',
        totalVolume: '',
        items: [],
      });

      // Refresh PO data to show new shipment
      fetchData();
    } catch (error) {
      console.error('Error creating shipment:', error);
      toast.error(getErrorMessage(error));
    } finally {
      setSavingShipment(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  /**
   * Delegates to the shared formatter so the currency comes from the record
   * rather than from a hardcoded 'USD' in this file. A page-local formatter is
   * how an order billed in dirhams ends up rendered with a dollar sign.
   */
  const formatCurrency = (amount?: number | null, currency = 'USD') =>
    amount == null ? '-' : formatMoney(amount, currency);

  if (loading) {
    return <LoadingState />;
  }

  if (!order) {
    return null;
  }

  const items = order.items || [];
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalReceived = items.reduce((sum, item) => sum + item.receivedQty, 0);
  const totalShipped = items.reduce((sum, item) => sum + item.shippedQty, 0);
  const receivedPercentage = totalItems > 0 ? Math.round((totalReceived / totalItems) * 100) : 0;
  const shippedPercentage = totalItems > 0 ? Math.round((totalShipped / totalItems) * 100) : 0;

  const itemColumns: Column<PurchaseOrderItemWithShipped>[] = [
    {
      key: 'item',
      header: 'Item',
      // The line references a stock item that has its own page.
      linkTo: (line) => (line.item?.id ? `/inventory/${line.item.id}` : null),
      render: (item) => (
        <div>
          <div className="font-medium">{item.item?.name || '-'}</div>
          <div className="text-xs leading-4 text-muted-foreground">{item.item?.sku}</div>
        </div>
      ),
    },
    {
      key: 'quantity',
      header: 'Ordered',
      render: (item) => (
        <span className="font-medium">
          {item.quantity} {item.item?.unit}
        </span>
      ),
    },
    {
      key: 'shipped',
      header: 'Shipped',
      render: (item) => {
        const percentage = item.quantity > 0 ? (item.shippedQty / item.quantity) * 100 : 0;
        return (
          <div className="space-y-1 min-w-[120px]">
            <div className="text-sm">
              {item.shippedQty} / {item.quantity} {item.item?.unit}
            </div>
            <Progress value={percentage} className="h-1.5" />
          </div>
        );
      },
    },
    {
      key: 'received',
      header: 'Received',
      render: (item) => {
        const percentage = item.quantity > 0 ? (item.receivedQty / item.quantity) * 100 : 0;
        return (
          <div className="space-y-1 min-w-[120px]">
            <div className="text-sm">
              {item.receivedQty} / {item.quantity} {item.item?.unit}
            </div>
            <Progress value={percentage} className="h-1.5" />
          </div>
        );
      },
    },
    {
      key: 'unitPrice',
      header: 'Unit Price',
      render: (item) => (
        <span className="text-muted-foreground">{formatCurrency(item.unitPrice)}</span>
      ),
    },
    {
      key: 'total',
      header: 'Total',
      render: (item) => (
        <span className="font-medium">{formatCurrency((item.unitPrice || 0) * item.quantity)}</span>
      ),
    },
  ];

  type ShipmentRow = PurchaseOrderWithShipments['shipments'][number];
  const shipmentColumns: Column<ShipmentRow>[] = [
    {
      key: 'shipmentNumber',
      header: 'Shipment Number',
      render: (shipment) => (
        <div>
          <div className="font-medium">{shipment.shipmentNumber}</div>
          {shipment.trackingNumber && (
            <div className="text-sm text-muted-foreground font-mono">{shipment.trackingNumber}</div>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (shipment) => (
        <Badge
          className={
            shipment.status === 'DELIVERED'
              ? 'bg-success-50 text-success-600'
              : shipment.status === 'IN_TRANSIT'
                ? 'bg-warning-50 text-warning-600'
                : shipment.status === 'DELAYED'
                  ? 'bg-danger-50 text-danger-500'
                  : 'bg-primary-50 text-primary-500'
          }
        >
          {shipment.status.replace('_', ' ')}
        </Badge>
      ),
    },
    {
      key: 'carrier',
      header: 'Carrier',
      render: (shipment) => <>{shipment.carrier || '-'}</>,
    },
    {
      key: 'route',
      header: 'Route',
      render: (shipment) => (
        <div className="text-sm">
          {shipment.origin?.name || '-'} → {shipment.destination?.name || '-'}
        </div>
      ),
    },
    {
      key: 'eta',
      header: 'ETA',
      render: (shipment) => (
        <>
          {shipment.actualArrival
            ? formatDate(shipment.actualArrival)
            : shipment.estimatedArrival
              ? formatDate(shipment.estimatedArrival)
              : '-'}
        </>
      ),
    },
    {
      key: 'items',
      header: 'Items',
      render: (shipment) => (
        <Badge variant="outline">
          {shipment.items?.length || 0} item{shipment.items?.length !== 1 ? 's' : ''}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (shipment) => (
        <Button variant="ghost" size="sm" asChild>
          <Link to={`/shipments/${shipment.id}`}>
            View <ExternalLink className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      ),
    },
  ];

  const getActionButtons = () => {
    switch (order.status) {
      case 'DRAFT':
        return (
          <>
            <Button
              variant="outline"
              onClick={() => setActionDialog({ open: true, action: 'cancel' })}
            >
              <XCircle className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button onClick={() => setActionDialog({ open: true, action: 'submit' })}>
              <Send className="mr-2 h-4 w-4" />
              Submit for Approval
            </Button>
          </>
        );
      case 'APPROVED':
        return (
          <Button onClick={() => setActionDialog({ open: true, action: 'issue' })}>
            <Truck className="mr-2 h-4 w-4" />
            Issue to Supplier
          </Button>
        );
      case 'ORDERED':
      case 'PARTIALLY_RECEIVED': {
        // Check if there are outstanding items to ship
        const hasOutstandingItems = order.items.some((item) => item.quantity > item.shippedQty);

        return (
          <>
            {hasOutstandingItems && (
              <Button onClick={handleOpenShipmentSheet} variant="outline">
                <Truck className="mr-2 h-4 w-4" />
                Create Shipment
              </Button>
            )}
            <Button>
              <CheckCircle className="mr-2 h-4 w-4" />
              Receive Items
            </Button>
          </>
        );
      }
      default:
        return null;
    }
  };

  const getDialogContent = () => {
    switch (actionDialog.action) {
      case 'submit':
        return {
          title: 'Submit for Approval',
          description: 'This will send the purchase order for approval. Are you sure?',
          confirmLabel: 'Submit',
          variant: 'default' as const,
        };
      case 'issue':
        return {
          title: 'Issue to Supplier',
          description: 'This will issue the purchase order to the supplier. Are you sure?',
          confirmLabel: 'Issue',
          variant: 'default' as const,
        };
      case 'cancel':
        return {
          title: 'Cancel Purchase Order',
          description: 'This will cancel the purchase order. This action cannot be undone.',
          confirmLabel: 'Cancel PO',
          variant: 'destructive' as const,
        };
      default:
        return { title: '', description: '', confirmLabel: '', variant: 'default' as const };
    }
  };

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          backLink={{ label: 'Back to Purchase Orders', href: '/purchase-orders' }}
          title={order.poNumber}
          description={`Created: ${formatDate(order.createdAt)}`}
          actions={<div className="flex gap-2">{getActionButtons()}</div>}
        />
      </motion.div>

      {/* KPI Cards */}
      <div className="mt-6">
        <KPIStrip columns={4} className="lg:grid-cols-5 xl:grid-cols-5">
          <MetricCard
            label="Total Amount"
            value={formatCurrency(order.totalAmount, order.currency)}
            sentiment="success"
            icon={<FileText className="h-5 w-5" />}
          />
          <MetricCard
            label="Items"
            value={items.length}
            sentiment="info"
            icon={<Package className="h-5 w-5" />}
          />
          <MetricCard
            label="Shipped"
            value={`${shippedPercentage}%`}
            sentiment={
              shippedPercentage === 100 ? 'success' : shippedPercentage > 0 ? 'warning' : 'neutral'
            }
            trend={
              shippedPercentage === 100
                ? { direction: 'up', value: '', text: 'complete' }
                : undefined
            }
          />
          <MetricCard
            label="Received"
            value={`${receivedPercentage}%`}
            sentiment={
              receivedPercentage === 100
                ? 'success'
                : receivedPercentage > 0
                  ? 'warning'
                  : 'neutral'
            }
            trend={
              receivedPercentage === 100
                ? { direction: 'up', value: '', text: 'complete' }
                : undefined
            }
          />
          <MetricCard
            label="Status"
            value={statusLabels[order.status] || order.status}
            sentiment="neutral"
          />
        </KPIStrip>
      </div>

      {/* Line items and the shipments fulfilling them are the work; who the
          order is with and when it is due is reference. The reference used to
          hold the narrow left column and the line items the wide right. */}
      <motion.div variants={fadeUp} className="mt-6">
        <DetailLayout
          rail={
            <RailCard
              title="Order details"
              icon={<FileText className="h-4 w-4 text-muted-foreground" />}
            >
              <DetailList
                items={[
                  {
                    label: 'Status',
                    value: <StatusBadge status={statusMap[order.status] || 'draft'} />,
                  },
                  { label: 'Supplier', value: order.supplier?.name || '—' },
                  { label: 'Order date', value: formatDate(order.orderDate) },
                  {
                    label: 'Expected',
                    value: formatDate(order.expectedDate),
                    // An expected date already in the past on an order that is
                    // not yet received is the one thing here worth colouring.
                    tone:
                      order.expectedDate &&
                      new Date(order.expectedDate) < new Date() &&
                      order.status !== 'RECEIVED' &&
                      order.status !== 'CANCELLED'
                        ? 'destructive'
                        : 'default',
                  },
                  { label: 'Currency', value: order.currency },
                  { label: 'Line items', value: items.length },
                ]}
              />

              {order.notes && (
                <div className="mt-4 border-t pt-3">
                  <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Notes
                  </p>
                  <p className="text-sm text-muted-foreground">{order.notes}</p>
                </div>
              )}
            </RailCard>
          }
          main={
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <h3 className="text-base font-semibold text-foreground">Line items</h3>
                  <CardDescription>
                    {items.length} {items.length === 1 ? 'item' : 'items'} in this order
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <DataTable
                    pagination={false}
                    data={items}
                    columns={itemColumns}
                    emptyTitle="No items"
                    emptyDescription="No items have been added to this order."
                  />
                </CardContent>
              </Card>

              {milestones.length > 0 && (
                <Card>
                  <CardHeader>
                    <h3 className="text-base font-semibold text-foreground">Milestones</h3>
                    <CardDescription>
                      Where this order has got to, from placement through receipt
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <MilestoneTimeline
                      milestones={milestones}
                      onUpdateMilestone={handleUpdateMilestone}
                    />
                  </CardContent>
                </Card>
              )}

              {order.shipments && order.shipments.length > 0 && (
                <Card>
                  <CardHeader>
                    <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
                      <Truck className="h-5 w-5" />
                      Shipments ({order.shipments.length})
                    </h3>
                    <CardDescription>Shipments fulfilling this purchase order</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <DataTable<ShipmentRow>
                      pagination={false}
                      data={order.shipments}
                      columns={shipmentColumns}
                    />
                  </CardContent>
                </Card>
              )}
            </div>
          }
        />
      </motion.div>

      {/* Action Dialog */}
      <ConfirmDialog
        open={actionDialog.open}
        onOpenChange={(open) => setActionDialog({ open })}
        {...getDialogContent()}
        onConfirm={handleAction}
        loading={processing}
      />

      {/* Create Shipment Sheet */}
      <Sheet open={shipmentSheetOpen} onOpenChange={setShipmentSheetOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Create Shipment from {order?.poNumber}</SheetTitle>
            <SheetDescription>
              Create a new shipment with outstanding items from this purchase order
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-6 py-6">
            {/* Origin Location */}
            <div className="space-y-2">
              <Label htmlFor="origin">Origin Location *</Label>
              <Select
                value={shipmentFormData.originId}
                onValueChange={(value) =>
                  setShipmentFormData({ ...shipmentFormData, originId: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select origin location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((location) => (
                    <SelectItem key={location.id} value={location.id}>
                      {location.name} ({location.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Destination Location */}
            <div className="space-y-2">
              <Label htmlFor="destination">Destination Location *</Label>
              <Select
                value={shipmentFormData.destinationId}
                onValueChange={(value) =>
                  setShipmentFormData({ ...shipmentFormData, destinationId: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select destination location" />
                </SelectTrigger>
                <SelectContent>
                  {locations
                    .filter((loc) => loc.id !== shipmentFormData.originId)
                    .map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.name} ({location.code})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Carrier Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="carrier">Carrier</Label>
                <Input
                  id="carrier"
                  placeholder="e.g., DHL, FedEx"
                  value={shipmentFormData.carrier}
                  onChange={(e) =>
                    setShipmentFormData({ ...shipmentFormData, carrier: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tracking">Tracking Number</Label>
                <Input
                  id="tracking"
                  placeholder="Optional"
                  value={shipmentFormData.trackingNumber}
                  onChange={(e) =>
                    setShipmentFormData({ ...shipmentFormData, trackingNumber: e.target.value })
                  }
                />
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="departure">Estimated Departure</Label>
                <Input
                  id="departure"
                  type="date"
                  value={shipmentFormData.estimatedDeparture}
                  onChange={(e) =>
                    setShipmentFormData({ ...shipmentFormData, estimatedDeparture: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="arrival">Estimated Arrival</Label>
                <Input
                  id="arrival"
                  type="date"
                  value={shipmentFormData.estimatedArrival}
                  onChange={(e) =>
                    setShipmentFormData({ ...shipmentFormData, estimatedArrival: e.target.value })
                  }
                />
              </div>
            </div>

            {/* Dimensions */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="weight">Total Weight (kg)</Label>
                <Input
                  id="weight"
                  type="number"
                  placeholder="Optional"
                  value={shipmentFormData.totalWeight}
                  onChange={(e) =>
                    setShipmentFormData({ ...shipmentFormData, totalWeight: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="volume">Total Volume (m³)</Label>
                <Input
                  id="volume"
                  type="number"
                  placeholder="Optional"
                  value={shipmentFormData.totalVolume}
                  onChange={(e) =>
                    setShipmentFormData({ ...shipmentFormData, totalVolume: e.target.value })
                  }
                />
              </div>
            </div>

            {/* Items Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base">Items *</Label>
                <Badge variant="secondary">{shipmentFormData.items.length} item(s)</Badge>
              </div>

              {shipmentFormData.items.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No outstanding items to ship
                </div>
              ) : (
                <div className="space-y-3">
                  {shipmentFormData.items.map((item, index) => (
                    <Card key={index}>
                      <CardContent className="pt-4 pb-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="font-medium">{item.item?.name || 'Unknown Item'}</div>
                            <div className="text-sm text-muted-foreground font-mono">
                              {item.item?.sku}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Quantity</Label>
                              <Input
                                type="number"
                                min="1"
                                max={item.maxQuantity}
                                value={item.quantity}
                                onChange={(e) => {
                                  const newQuantity = parseInt(e.target.value) || 0;
                                  const updatedItems = [...shipmentFormData.items];
                                  updatedItems[index].quantity = Math.min(
                                    Math.max(1, newQuantity),
                                    item.maxQuantity
                                  );
                                  setShipmentFormData({ ...shipmentFormData, items: updatedItems });
                                }}
                                className="w-24"
                              />
                              <div className="text-xs text-muted-foreground">
                                Max: {item.maxQuantity} {item.item?.unit}
                              </div>
                            </div>
                            <Button
                              aria-label="Remove item"
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                const updatedItems = shipmentFormData.items.filter(
                                  (_, i) => i !== index
                                );
                                setShipmentFormData({ ...shipmentFormData, items: updatedItems });
                              }}
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>

          <SheetFooter>
            <Button
              variant="outline"
              onClick={() => setShipmentSheetOpen(false)}
              disabled={savingShipment}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateShipment} disabled={savingShipment}>
              {savingShipment ? 'Creating...' : 'Create Shipment'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </motion.main>
  );
}
